<template>
  <div class="analisis-vendedores">
    <h3 class="text-2xl font-bold text-gray-800 mb-6">👥 Inteligencia de Vendedores</h3>
    
    <!-- Ranking de Performance -->
    <div class="space-y-4 mb-8">
      <div
        v-for="(vendedor, index) in ranking"
        :key="index"
        class="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300"
      >
        <div class="flex items-start gap-6">
          <!-- Ranking Badge -->
          <div :class="[
            'flex-shrink-0 w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-black',
            index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-white' :
            index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-white' :
            index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white' :
            'bg-gradient-to-br from-blue-100 to-blue-200 text-blue-800'
          ]">
            {{ index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : '#' + (index + 1) }}
          </div>

          <!-- Info del Vendedor -->
          <div class="flex-1">
            <div class="flex items-start justify-between mb-4">
              <div>
                <h4 class="text-xl font-bold text-gray-900 mb-1">{{ vendedor.nombre }}</h4>
                <p class="text-sm text-gray-500">{{ vendedor.email }}</p>
              </div>
              <div class="text-right">
                <div :class="[
                  'px-4 py-2 rounded-xl font-bold text-sm inline-block',
                  vendedor.clasificacion === 'EXCELENTE' ? 'bg-green-100 text-green-800' :
                  vendedor.clasificacion === 'BUENO' ? 'bg-blue-100 text-blue-800' :
                  'bg-yellow-100 text-yellow-800'
                ]">
                  {{ vendedor.clasificacion }}
                </div>
                <div class="text-3xl font-black text-gray-900 mt-2">{{ vendedor.performance_score }}</div>
                <div class="text-xs text-gray-500">Performance Score</div>
              </div>
            </div>

            <!-- Métricas -->
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div class="bg-blue-50 rounded-xl p-3">
                <div class="text-xs text-blue-600 font-semibold mb-1">Cotizaciones</div>
                <div class="text-2xl font-bold text-blue-900">{{ vendedor.metricas.cotizaciones }}</div>
              </div>
              <div class="bg-green-50 rounded-xl p-3">
                <div class="text-xs text-green-600 font-semibold mb-1">Ventas</div>
                <div class="text-2xl font-bold text-green-900">{{ vendedor.metricas.ventas_cerradas }}</div>
              </div>
              <div class="bg-purple-50 rounded-xl p-3">
                <div class="text-xs text-purple-600 font-semibold mb-1">Conversión</div>
                <div class="text-2xl font-bold text-purple-900">{{ vendedor.metricas.tasa_conversion }}%</div>
              </div>
              <div class="bg-orange-50 rounded-xl p-3">
                <div class="text-xs text-orange-600 font-semibold mb-1">Ingresos</div>
                <div class="text-2xl font-bold text-orange-900">${{ formatNumber(vendedor.metricas.ingresos) }}</div>
              </div>
            </div>

            <!-- Fortalezas y Áreas de Mejora -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <div class="text-xs font-bold text-gray-700 mb-2">💪 Fortalezas</div>
                <div class="flex flex-wrap gap-2">
                  <span
                    v-for="(fortaleza, i) in vendedor.fortalezas"
                    :key="i"
                    class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold"
                  >
                    {{ formatTag(fortaleza) }}
                  </span>
                </div>
              </div>
              <div>
                <div class="text-xs font-bold text-gray-700 mb-2">📈 Áreas de Mejora</div>
                <div class="flex flex-wrap gap-2">
                  <span
                    v-for="(area, i) in vendedor.areas_mejora"
                    :key="i"
                    class="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-semibold"
                  >
                    {{ formatTag(area) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Recomendaciones IA -->
            <div class="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200">
              <div class="text-xs font-bold text-indigo-700 mb-2 flex items-center gap-2">
                <span>🤖</span> Recomendaciones de IA
              </div>
              <ul class="space-y-1">
                <li
                  v-for="(rec, i) in vendedor.recomendaciones_ia"
                  :key="i"
                  class="text-sm text-gray-700 flex items-start gap-2"
                >
                  <span class="text-indigo-600">•</span>
                  <span>{{ rec }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Análisis del Equipo -->
    <div v-if="analisisEquipo" class="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
      <h4 class="text-lg font-bold text-gray-800 mb-4">📊 Análisis del Equipo</h4>
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div class="bg-white rounded-xl p-4 shadow">
          <div class="text-sm text-gray-600 mb-1">Performance Promedio</div>
          <div class="text-3xl font-bold text-indigo-600">{{ analisisEquipo.performance_promedio }}</div>
        </div>
        <div class="bg-white rounded-xl p-4 shadow">
          <div class="text-sm text-gray-600 mb-1">Vendedores Top</div>
          <div class="text-3xl font-bold text-green-600">{{ analisisEquipo.vendedores_top }}</div>
        </div>
        <div class="bg-white rounded-xl p-4 shadow">
          <div class="text-sm text-gray-600 mb-1">Necesitan Coaching</div>
          <div class="text-3xl font-bold text-yellow-600">{{ analisisEquipo.necesitan_coaching }}</div>
        </div>
        <div class="bg-white rounded-xl p-4 shadow">
          <div class="text-sm text-gray-600 mb-1">Mejor Vendedor</div>
          <div class="text-lg font-bold text-purple-600">{{ analisisEquipo.mejor_vendedor }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AnalisisVendedores',
  props: {
    vendedores: {
      type: Object,
      required: true
    }
  },
  computed: {
    ranking() {
      return this.vendedores?.ranking_performance || []
    },
    analisisEquipo() {
      return this.vendedores?.analisis_equipo || null
    }
  },
  methods: {
    formatNumber(num) {
      if (!num) return '0'
      return new Intl.NumberFormat('es-MX').format(num)
    },
    formatTag(tag) {
      return tag.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }
  }
}
</script>
